<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useLocalizedRouter } from '@/composables/localizedRouter'
import NavBarDesktop from './NavBarDesktop/NavBarDesktop.vue'
import { Capacitor } from '@capacitor/core'
import { useCartStore } from "@/stores/useCartStore"
import { useSessionStore } from "@/stores/useSessionStore"
import NavBarMobile from "@/components/Navigation/NavBar/NavBarMobile.vue"
import { establishmentPageRouteParams } from "@/utils/routing/helpers"
import { Distribution } from "@/enums/Distribution"
import { storeToRefs } from "pinia"

const route = useRoute()
const cartStore = useCartStore()
const sessionStore = useSessionStore()
const { localizedRoute } = useLocalizedRouter()

const { establishment: establishmentInCart } = storeToRefs(cartStore)

const doNotShowMobileHeader = computed(() =>
    String(route.name).includes('establishment')
    || String(route.name).includes('thanks')
    || String(route.name).includes('checkout')
    || String(route.name).includes('slug')
    || String(route.name).includes('marketplace'),
)

const isMarketplacePage = computed(() => String(route.name).includes('marketplace') || String(route.name).includes('slug'))

const logoLink = computed(() => {
  const name = String(route.name)

  if (name?.includes('checkout') && establishmentInCart.value !== null) {
    return localizedRoute({
      name: 'establishment',
      params: {
        ...establishmentPageRouteParams(establishmentInCart.value),
        onLocationId: sessionStore.distribution === Distribution.OnLocation ? sessionStore.onLocationIdentifier : undefined,
      },
    })
  }

  return name.includes('marketplace')
      ? Capacitor.isNativePlatform() ? localizedRoute({ name: 'home' }) : '/'
      : localizedRoute({
        name: 'marketplace',
      })
})

const isEstablishmentPage = computed(() => String(route.name).includes('establishment'))
const isThanksPage = computed(() => String(route.name).includes('thanks'))
const isCheckoutPage = computed(() => String(route.name).includes('checkout'))
</script>

<template>
  <header data-test-id="navbar"
          :class="[ 'navbar', { 'navbar--marketplace': isMarketplacePage, 'navbar--establishment': isEstablishmentPage, 'navbar--thanks': isThanksPage, 'navbar--checkout': isCheckoutPage } ]">
    <NavBarDesktop :logo-link="logoLink"/>

    <NavBarMobile :logo-link="logoLink"
                  v-if="!doNotShowMobileHeader"/>
  </header>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/breakpoints-down.scss";

.navbar {
  $self:               &;
  width:               100%;
  position:            fixed;

  left:                0;
  top:                 0;
  right:               0;
  z-index:             50;
  background-color:    #ffffff;

  // We increase the height of the header and nudge it up, to prevent elements from showing up above the header when scrolling vigorously on iOs
  padding:             1rem 0 0 0;
  margin-top:          -1rem;

  // place this div on a 3D layer
  transform:           translate3d(0, 0, 0);
  backface-visibility: hidden;
  -webkit-overflow-scrolling: touch;

  @include lg-up {
    position: absolute;
    background: none;
    margin-top: 5rem;
  }

  // Hide on establishment pages & mobile views
  &--establishment,
  &--checkout,
  &--thanks {
    display: none;

    @include lg-up {
      display: block;
    }
  }

  &--marketplace {
    @include lg-down {
      background: var(--color-bg-base);
    }
  }
}
</style>
